import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import styles from './ContentOverview.module.css';

const BLOCK_DURATION = 0.2;

const AWARD_IMAGES = [
    'app_of_the_week.png',
    'best_gen_design_app.png',
    'hall_of_fame.png',
    'featured_in_ars_technica.png',
    'number_1_app.png',
    'featured_in_wired.png',
] as const;

function awardAltFromFile(file: (typeof AWARD_IMAGES)[number]): string {
    const base = file.replace(/\.png$/i, '').replace(/_/g, ' ');
    return base.replace(/\b\w/g, (c) => c.toUpperCase());
}

function isRoughlyOnScreen(el: HTMLElement | null): boolean {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    return rect.bottom > 0 && rect.top < vh && rect.right > 0 && rect.left < vw;
}

interface ContentOverviewProps {
    phase: 'in' | 'out';
    transitionToken: number;
    onHideComplete: (id: string, token: number) => void;
}

const ContentOverview: React.FC<ContentOverviewProps> = ({
    phase,
    transitionToken,
    onHideComplete,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const blockerControls = useAnimationControls();
    const [textOpacity, setTextOpacity] = useState(0);
    const cancelPaintRef = useRef(false);
    const completedHideTokenRef = useRef<number | null>(null);

    const runRevealPaint = useCallback(
        async (shouldWipe: boolean) => {
            cancelPaintRef.current = false;

            if (!shouldWipe) {
                blockerControls.set({ scaleX: 0, transformOrigin: 'left center' });
                setTextOpacity(1);
                return;
            }

            await blockerControls.start({
                scaleX: 1,
                transformOrigin: 'left center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
            if (cancelPaintRef.current) return;
            setTextOpacity(1);
            await blockerControls.start({
                scaleX: 0,
                transformOrigin: 'right center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
        },
        [blockerControls],
    );

    const runHidePaint = useCallback(
        async (shouldWipe: boolean, token: number) => {
            cancelPaintRef.current = false;

            if (!shouldWipe) {
                setTextOpacity(0);
                onHideComplete('overview', token);
                return;
            }

            blockerControls.set({ scaleX: 0, transformOrigin: 'left center' });
            await blockerControls.start({
                scaleX: 1,
                transformOrigin: 'left center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
            if (cancelPaintRef.current) return;
            setTextOpacity(0);
            await blockerControls.start({
                scaleX: 0,
                transformOrigin: 'right center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
            if (!cancelPaintRef.current) {
                onHideComplete('overview', token);
            }
        },
        [blockerControls, onHideComplete],
    );

    useEffect(() => {
        if (phase !== 'in') return;

        cancelPaintRef.current = false;
        const shouldWipe = isRoughlyOnScreen(containerRef.current);
        void runRevealPaint(shouldWipe);

        return () => {
            cancelPaintRef.current = true;
            blockerControls.stop();
        };
    }, [blockerControls, phase, runRevealPaint]);

    useEffect(() => {
        if (phase !== 'out') return;

        if (completedHideTokenRef.current === transitionToken) return;
        completedHideTokenRef.current = transitionToken;

        cancelPaintRef.current = false;
        const shouldWipe = isRoughlyOnScreen(containerRef.current);
        void runHidePaint(shouldWipe, transitionToken);

        return () => {
            cancelPaintRef.current = true;
            blockerControls.stop();
        };
    }, [blockerControls, phase, runHidePaint, transitionToken]);

    return (
        <div ref={containerRef} className={styles.container}>
            <motion.div
                className={styles.text}
                initial={{ opacity: 0 }}
                animate={{ opacity: textOpacity }}
                transition={{ duration: 0 }}
            >
                <div className={styles.title}>Design + Engineering + Systems&nbsp;Thinking</div>
                
                I’m a design engineer who builds interactive systems, from award winning iOS apps to enterprise tools, simulations, and real time visualizations. My work lives at the intersection of design, engineering, and experimentation.
                
                <div className={styles.awards}>
                    {AWARD_IMAGES.map((file) => (
                        <img
                            key={file}
                            className={styles.awardImage}
                            src={`/images/award-images/${file}`}
                            alt={awardAltFromFile(file)}
                        />
                    ))}
                </div>
            </motion.div>
            <motion.div
                className={styles.blocker}
                aria-hidden
                initial={{ scaleX: 0 }}
                animate={blockerControls}
                style={{ transformOrigin: 'left center' }}
            />
        </div>
    );
};

export default ContentOverview;
